import React from "react";

import { Alert } from "~/components/alert";
import { Button } from "~/components/button";
import { Editor } from "~/components/code/editor";
import { Form } from "~/components/form";
import { FormField } from "~/components/form/field";
import { FormMessage } from "~/components/form/message";

import type { DashboardSpec } from "../../common/dashboard-spec";
import { DashboardSaveAlert } from "../../routes/dashboards/dashboard/save-alert";
import { CloseDrawerButton, DrawerTitle, ExplorationDrawer } from "../react/drawer/ExplorationDrawer";

import type { DrawerState } from "./_drawer-state";
import { FORM_ID } from "./_form-id";

export interface DrawerProps {
	when: undefined | DrawerState;
	id: string;
	onSave: (spec: DashboardSpec) => void;
	onClose: VoidFunction;
}

export const Drawer: React.FC<DrawerProps> = ({ when, id, onSave, onClose }) => (
	<ExplorationDrawer
		footer={
			<>
				<CloseDrawerButton />
				<Button className="uppercase" form={FORM_ID} type="submit" variant="primary">
					Update dashboard definition
				</Button>
			</>
		}
		headerTitle={<DrawerTitle title="Edit Dashboard YAML" />}
		id={id}
		layout="full"
		onClose={onClose}
		when={when}
	>
		{(content, _, preservedContent) => {
			const state = preservedContent ?? content;

			return (
				<Form
					className="h-full"
					id={FORM_ID}
					onSubmit={(output) => {
						onSave(output);
						onClose();
					}}
					state={state}
				>
					<FormField className="flex h-full flex-col items-stretch" state={state}>
						<DashboardSaveAlert className="mb-4" placement="dashboard-yaml-drawer" />

						{(monitor, field) => (
							<div className="min-h-0 flex-1">
								<Editor
									height="100%"
									language="yaml"
									onChange={(input) => field.setInput(input)}
									value={field.getInput(monitor)}
								/>
							</div>
						)}

						{(monitor, field) => {
							const error = field.getError(monitor);

							if (error == null) {
								return null;
							}

							return (
								<FormMessage asChild unstyled variant="error">
									<Alert variant="error">{error}</Alert>
								</FormMessage>
							);
						}}
					</FormField>
				</Form>
			);
		}}
	</ExplorationDrawer>
);
